// import React, { useEffect, useState } from 'react';
import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import UserActionsBar from '../components/UserActionsBar'; // Nueva importación
import LogoutButton from '../components/LogoutButton';

const Activity = () => {
   const { isLoading: isContextLoading, isAuthenticated } = useUser();
   const navigate = useNavigate();
   const [activities, setActivities] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [currentPage, setCurrentPage] = useState(1);
   const activitiesPerPage = 10;
   // const ON_CHAIN_ID_PLACEHOLDER = "0x...placeholder...";
   // const ADDRESS_PLACEHOLDER = "0x...placeholder...";
   const ON_CHAIN_ID_PLACEHOLDER = "No blockchain ID";
   const ADDRESS_PLACEHOLDER = "No wallet address";

   const API_BASE_URL = 'https://pt-api.digit-a.net/api'

   const cryptoLogos = {
      BTC: '/assets/img/icon_btc.svg',
      ETH: '/assets/img/icon_eth.svg',
      PROPT: '/assets/img/icon_propt_n.svg',
      USD: '/assets/img/icon_propt_dollar.svg',
      USDC: '/assets/img/icon_usdc.svg',
      USDT: '/assets/img/icon_usdt.svg',
      POL: '/assets/img/icon_pol.svg',
      DEFAULT: '/assets/img/icon_dollar.svg',
   };

   const handleCopyToClipboard = async (textToCopy) => {
      if (!navigator.clipboard) {
         // Fallback para navegadores muy antiguos
         alert('Clipboard API not available in this browser.');
         return;
      }
      try {
         await navigator.clipboard.writeText(textToCopy);
         alert('Copied to clipboard!');
      } catch (err) {
         console.error('Failed to copy text: ', err);
         alert('Failed to copy.');
      }
   };



   const getStatusBadgeClass = (status) => {
      const s = status?.toLowerCase();
      switch (s) {
         case 'success':
            return 'bg-success'; // Verde
         case 'pending':
            return 'bg-warning text-dark'; // Naranja/Amarillo 
         case 'failed':
            return 'bg-danger'; // Rojo
         default:
            return 'bg-secondary'; // Gris para cualquier otro caso
      }
   };

   useEffect(() => {
      // console.log('[useEffect] Context Loading:', isContextLoading);
      // console.log('[useEffect] isAuthenticated:', isAuthenticated);
      // Esperamos hasta que termine de cargar el contexto
      if (isContextLoading) {
         // console.log('[useEffect] Context still loading, skipping...');
         return;
      }

      if (!isAuthenticated) {
         // console.log('[useEffect] Not authenticated, redirecting to /sign-in');
         navigate('/sign-in');
         return;
      }

      const fetchActivities = async () => {
         // console.log('[fetchActivities] Starting fetch...');
         setIsLoading(true);
         const jwt = localStorage.getItem('jwt');
         const email = localStorage.getItem('userEmail');
         // console.log('[fetchActivities] JWT:', jwt);
         // console.log('[fetchActivities] Email:', email);

         if (!jwt || !email) {
            // console.log('[fetchActivities] Missing JWT or email. Aborting.');
            setIsLoading(false);
            return;
         }

         try {
            const responses = await Promise.all([
               fetch(`${API_BASE_URL}/transactions/get-transactions`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({})
               }),
               fetch(`${API_BASE_URL}/withdraw/get-withdrawals`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({})
               }),
               fetch(`${API_BASE_URL}/history/on-chain`, {
                  method: 'GET',
                  headers: {
                     'Authorization': `Bearer ${jwt}`,
                     'Content-Type': 'application/json'
                  }
               })
            ]);

            for (const res of responses) {
               // console.log(`[fetchActivities] Response status: ${res.status}`);
               if (!res.ok) throw new Error(`API request failed: ${res.status}`);
            }

            const [depositsData, withdrawalsData, onChainData] = await Promise.all(
               responses.map(res => res.json())
            );
            // console.log('[fetchActivities] Deposits:', depositsData);
            // console.log('[fetchActivities] Withdrawals:', withdrawalsData);
            // console.log('[fetchActivities] OnChain:', onChainData);


            const deposits = (depositsData.data || [])
               .filter(tx => tx.user === email)
               .map(tx => ({
                  id: tx.uuid || tx.codeTX,
                  type: 'Deposit',
                  status: tx.status,
                  amount: tx.amount,
                  createdAt: tx.createdAt,
                  currency: tx.currency,
                  onChainId: tx.transactionHash,
                  address: tx.address,
                  blockchain: tx.network,
               }));

            const withdrawals = (withdrawalsData.data || [])
               .filter(tx => tx.user === email)
               .map(tx => ({
                  id: tx.uuid || tx.codeWithdraw,
                  type: 'Withdrawal',
                  status: tx.status,
                  amount: tx.amount,
                  createdAt: tx.createdAt,
                  currency: tx.currency,
                  onChainId: tx.transactionHash,
                  address: tx.address,
                  blockchain: tx.network,
               }));

            const onChainHistory = (onChainData.data || []).map(tx => ({
               id: tx.onChainId,
               type: tx.type.charAt(0).toUpperCase() + tx.type.slice(1),
               status: tx.status,
               amount: tx.amount,
               currency: tx.currency,
               createdAt: tx.date,
               blockchain: tx.network,
               address: tx.address,
               onChainId: tx.onChainId,
            }));

            const combined = [...deposits, ...withdrawals, ...onChainHistory]
               .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setActivities(combined);

         } catch (error) {
            console.error("Error fetching activities:", error);
         } finally {
            // console.log('[fetchActivities] Fetch complete');
            setIsLoading(false);
         }
      };

      fetchActivities();
   }, [isAuthenticated, isContextLoading, navigate]);


   const indexOfLastActivity = currentPage * activitiesPerPage;
   const indexOfFirstActivity = indexOfLastActivity - activitiesPerPage;
   const currentActivities = activities.slice(indexOfFirstActivity, indexOfLastActivity);
   const paginate = (pageNumber) => setCurrentPage(pageNumber);

   if (isContextLoading) {
      return <div>Loading...</div>;
   }
   if (isLoading) {
      return <div>Loading...</div>;
   }
   if (!isAuthenticated) {
      return null;
   }
   // console.log('[Render] isContextLoading:', isContextLoading);
   // console.log('[Render] isAuthenticated:', isAuthenticated);
   // console.log('[Render] isLoading:', isLoading);


   return (
      <div className="container-fluid max py-lg-5" page-key="Dashboard">
         <nav className="navbar navbar-expand-lg p-0 mb-4 mb-lg-5">
            <div className="container-fluid px-0">
               <div className="row align-items-center w-100 mx-0" data-orders>
                  <div data-order="1" className="col col-lg-3 px-0">
                     <NavLink to="/dashboard" className="">
                        <img src={process.env.PUBLIC_URL + '/assets/img/logo_propert.svg'} data-lightmodeimg alt="ProperT" border="0" />
                     </NavLink>
                  </div>
                  <div data-order="2" className="col-auto d-flex align-items-center gap-3 px-0">
                     <UserActionsBar />
                     <span className="d-lg-none" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNavDropdown" aria-controls="navbarNavDropdown" aria-expanded="false" aria-label="Toggle navigation">
                        <svg width="28" height="23" viewBox="0 0 28 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                           <rect x="0.577148" width="27.4231" height="3.53846" fill="var(--white)" />
                           <rect x="0.577148" y="9.73077" width="27.4231" height="3.53846" fill="var(--white)" />
                           <rect x="0.577148" y="19.4615" width="27.4231" height="3.53846" fill="var(--white)" />
                        </svg>
                     </span>
                  </div>
                  <div data-order="3" className="collapse navbar-collapse" id="navbarNavDropdown">
                     <div className="col">
                        <nav className="ppt_navdash">
                           <NavLink to="/dashboard" className="link">Wallet</NavLink>
                           <NavLink to="/propt-tokens" className="link">Propt Tokens</NavLink>
                           {/* <span className="link">Earn</span> */}
                           <NavLink to="/support" className="link">SUPPORT</NavLink>
                           <NavLink to="/settings" className="link d-lg-none">SETTINGS</NavLink>
                           <span className="link d-lg-none">
                              <LogoutButton />
                           </span>
                        </nav>
                     </div>
                  </div>
               </div>
            </div>
         </nav>
         <div className="row mb-lg-5 flex-column-reverse pt-3">
            <div className="col col-lg-3">
               <div className="ppt_header d-flex justify-content-between align-items-center">
                  <nav className="ppt_navdash_scd">
                     <NavLink to="/dashboard" className="link">Balances</NavLink>
                     <NavLink to="/activity" className="link">Activity</NavLink>
                     {/* <NavLink to="/exchange" className="link">Exchange</NavLink> */}
                     {/* <NavLink to="/deposit" className="link">Deposit</NavLink> */}
                     {/* <NavLink to="/withdrawal" className="link">Withdrawal</NavLink> */}
                  </nav>
               </div>
            </div>
            {/* <div className="col-12 col-lg d-flex gap-3 align-items-start justify-content-end ppt_account_actions_btns mb-4 mb-lg-0">
               <NavLink
                  className="ppt_btn_secondary d-flex align-items-center gap-2 justify-content-center"
                  to="."
                  onClick={handleWithdrawClick}
                  style={{ padding: '13px 18px', fontSize: '14px' }}
               >
                  <svg width="11" height="14" viewBox="0 0 11 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                     <path d="M5.5 13.5L5.5 1.5" stroke="#E4DC00" strokeWidth="2" strokeLinejoin="round" />
                     <path d="M10 5.5L5.5 1L1 5.5" stroke="#E4DC00" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                  Withdraw
               </NavLink>
               <NavLink
                  className="ppt_btn_secondary d-flex align-items-center gap-2 justify-content-center"
                  to="."
                  onClick={handleDepositClick}
                  style={{ padding: '13px 18px', fontSize: '14px' }}
               >
                  <svg width="11" height="14" viewBox="0 0 11 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                     <path d="M5.5 0V12" stroke="#E4DC00" strokeWidth="2" strokeLinejoin="round" />
                     <path d="M1 8L5.5 12.5L10 8" stroke="#E4DC00" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                  Deposit
               </NavLink>
            </div> */}
         </div>
         <div className="row mb-5">
            <div className="col">
               <hr />
               <div className="ppt_box_gen_wrap d-flex flex-column gap-3">
                  {isLoading ? (
                     <p>Loading activities...</p>
                  ) : currentActivities.length > 0 ? (
                     currentActivities.map(activity => {
                        const date = new Date(activity.createdAt).toLocaleDateString('es-ES');
                        const time = new Date(activity.createdAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                        const isDeposit = activity.type === 'Deposit';
                        // Seleccionamos el logo, con uno por defecto si la moneda no se encuentra
                        const logo = cryptoLogos[activity.currency] || cryptoLogos['DEFAULT'];

                        return (
                           <div className="ppt_box_gen" key={activity.id}>
                              <div className="d-flex justify-content-between align-items-start gap-3 ppt_cryptoinfo">

                                 {/* Columna 1 y 2 (Tipo y Monto) */}
                                 <span className='ppt_box_gen_col d-flex gap-4 align-items-start' style={{ flexBasis: '360px', flexShrink: 0 }}>
                                    <div className='d-flex align-items-start gap-2 col'>

                                       {/* -------- Tipo ------------- */}
                                       <img src={process.env.PUBLIC_URL + logo} alt={activity.currency} style={{ width: '31px' }} />
                                       <div className="ppt_cryptoinfo_title">
                                          {
                                             activity.currency ?
                                                <span><b>{activity.currency} {activity.type}</b></span>
                                                :
                                                <span><b>Old {activity.type}</b></span>
                                          }
                                          <small>{date}</small>
                                          <small><b>Status:</b>
                                             <span className={`badge ${getStatusBadgeClass(activity.status)} ms-2`}>
                                                {activity.status}
                                             </span>
                                          </small>
                                       </div>
                                    </div>

                                    {/* ---------- Deposito o Retiros ----------------- */}
                                    <div className='ppt_box_gen_col d-flex flex-column gap-1 col-4'>
                                       {activity.currency === "USD" ?
                                          <small><b>{isDeposit ? '+' : '-'}{activity.amount.toFixed(2)} {activity.currency}</b></small>
                                          : activity.currency === "POL" ?
                                             <small><b>{isDeposit ? '+' : '-'}{activity.amount.toFixed(4)} {activity.currency}</b></small>
                                             :
                                             <small><b>{isDeposit ? '+' : '-'}{activity.amount.toFixed(4)} {activity.currency}</b></small>
                                       }
                                    </div>
                                 </span>

                                 {/* ---------------------- Hora ------------------------- */}
                                 <div className='ppt_box_gen_col d-none d-lg-flex flex-column gap-1'>
                                    <small><b>Hour</b></small>
                                    <small>{time}</small>
                                 </div>

                                 {/* --------------- Nombre del blockchain ----------------- */}
                                 {activity.blockchain ?
                                    <div className='ppt_box_gen_col d-none d-lg-flex flex-column gap-1'>
                                       <small><b>Blockchain</b></small>
                                       <small className="d-flex align-items-center gap-2">
                                          <img src={process.env.PUBLIC_URL + logo} alt={activity.blockchain} style={{ width: '18px' }} /> {activity.blockchain}
                                       </small>
                                    </div>
                                    :
                                    <div className='ppt_box_gen_col d-none d-lg-flex flex-column gap-1'>
                                       <small><b>Blockchain</b></small>
                                       <small className="d-flex align-items-center gap-2">
                                          None
                                       </small>
                                    </div>
                                 }

                                 {/* --------------- Direccion del deposit/transferencia ---------- */}
                                 {
                                    activity.address ?
                                       <div className='ppt_box_gen_col d-flex flex-column gap-1'>
                                          <small className="d-flex align-items-center gap-2">
                                             <b>{activity.type} Address</b>
                                             {/* Icono de Copiar para la Dirección */}
                                             <img
                                                src={process.env.PUBLIC_URL + '/assets/img/icon_copy_ylw.svg'}
                                                alt="Copy"
                                                style={{ width: '17px', cursor: 'pointer' }}
                                                onClick={() => handleCopyToClipboard(activity.address)}
                                             />
                                          </small>
                                          <small><span style={{ wordBreak: 'break-all' }}>{activity.address}</span></small>
                                       </div>
                                       :
                                       <div className='ppt_box_gen_col d-flex flex-column gap-1'>
                                          <small className="d-flex align-items-center gap-2">
                                             <b>{activity.type} Address</b>
                                             {/* Icono de Copiar para la Dirección */}
                                             <img
                                                src={process.env.PUBLIC_URL + '/assets/img/icon_copy_ylw.svg'}
                                                alt="Copy"
                                                style={{ width: '17px', cursor: 'pointer' }}
                                                onClick={() => handleCopyToClipboard(ADDRESS_PLACEHOLDER)}
                                             />
                                          </small>
                                          <small><span style={{ wordBreak: 'break-all' }}>{ADDRESS_PLACEHOLDER}</span></small>
                                       </div>
                                 }

                                 {/* ----------------- on chain id ------------------------- */}
                                 {
                                    activity.onChainId ?
                                       <div className='ppt_box_gen_col d-flex flex-column gap-1'>
                                          <small className="d-flex align-items-center gap-2">
                                             <b>On-chain transaction ID</b>
                                             {/* Icono de Copiar para el ID de Transacción */}
                                             <img
                                                src={process.env.PUBLIC_URL + '/assets/img/icon_copy_ylw.svg'}
                                                alt="Copy"
                                                style={{ width: '17px', cursor: 'pointer' }}
                                                onClick={() => handleCopyToClipboard(activity.onChainId)}
                                             />
                                          </small>
                                          <small><span style={{ wordBreak: 'break-all' }}>{activity.onChainId}</span></small>
                                       </div>
                                       :
                                       <div className='ppt_box_gen_col d-flex flex-column gap-1'>
                                          <small className="d-flex align-items-center gap-2">
                                             <b>On-chain transaction ID</b>
                                             {/* Icono de Copiar para el ID de Transacción */}
                                             <img
                                                src={process.env.PUBLIC_URL + '/assets/img/icon_copy_ylw.svg'}
                                                alt="Copy"
                                                style={{ width: '17px', cursor: 'pointer' }}
                                                onClick={() => handleCopyToClipboard(ON_CHAIN_ID_PLACEHOLDER)}
                                             />
                                          </small>
                                          <small><span style={{ wordBreak: 'break-all' }}>{ON_CHAIN_ID_PLACEHOLDER}</span></small>
                                       </div>
                                 }

                              </div>
                           </div>

                        );
                     })
                  ) : (
                     <p>No activity yet.</p>
                  )}
               </div>
               <div className="pagination d-flex justify-content-center mt-4">
                  {Array.from({ length: Math.ceil(activities.length / activitiesPerPage) }, (_, i) => (
                     <button key={i + 1} onClick={() => paginate(i + 1)} className={`btn ${currentPage === i + 1 ? 'btn-warning' : 'btn-outline-warning'} mx-1`}>
                        {i + 1}
                     </button>
                  ))}
               </div>
            </div>
         </div>
      </div>
   );
};
export default Activity;